/**
 * MatterPricingSummary - the READING of a matter's pricing, with no editor.
 *
 * Pricing is now edited in ONE place (the Economics tab's side panel), so the
 * read view had to stop being a private part of the Details card: Economics
 * shows the same block, and two hand-written copies of "how is this billed"
 * would drift the moment either was touched.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiClockLight,
  PiCoinsLight,
  PiHandshakeLight,
  PiPercentLight,
} from 'react-icons/pi';
import { readBudget } from './budgetModel';
import { formatCurrency } from '../../../utils/formatCurrency';
import { Field, FieldGrid, Well } from '../../shared/brand/Field';
import {
  PRICING_MODEL_LABEL_KEYS,
  requiresFixedFee,
  requiresContingency,
  resolvePricingModel,
} from './economicsConstants';
import type { MatterEconomicsSummary, PricingModel } from '../../../types/economics';

/**
 * A pricing model reads faster as a mark than as a word - the four are
 * distinguishable at a glance, which is what this block is for.
 */
export const PRICING_MODEL_ICON: Record<PricingModel, React.ReactNode> = {
  hourly: <PiClockLight />,
  fixed_fee: <PiCoinsLight />,
  contingency: <PiPercentLight />,
  hybrid: <PiHandshakeLight />,
};

export interface MatterPricingSummaryProps {
  matterData: any;
  /** Already-resolved lowercase currency code (see the callers' resolution order). */
  currency: string;
  /** Already-resolved budget: the matter blob's, else the rollup's. */
  budgetAmount: number | null;
  /** Read ONLY for the used/remaining hint under the amount. */
  economics?: MatterEconomicsSummary | null;
  /** Softens the "no budget" line for viewers who cannot set one. */
  canManage?: boolean;
}

const MatterPricingSummary: React.FC<MatterPricingSummaryProps> = ({
  matterData,
  currency,
  budgetAmount,
  economics,
  canManage = false,
}) => {
  const { t } = useTranslation();
  const model = resolvePricingModel(matterData, economics);
  const currencyCode = (currency || '').toUpperCase();
  const budgetReading = readBudget(economics, budgetAmount);

  return (
    <div className="space-y-4">
      {/* How it is billed - the headline. */}
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full
                         bg-surface-sunken/70 border border-hairline-soft
                         text-champagne [&>svg]:h-4 [&>svg]:w-4">
          {PRICING_MODEL_ICON[model]}
        </span>
        <div className="min-w-0">
          <p className="text-base font-medium text-fg-strong truncate">
            {t(PRICING_MODEL_LABEL_KEYS[model])}
          </p>
          <p className="text-[11px] text-fg-muted">
            {currencyCode
              ? t('Billed in {{currency}}', { currency: currencyCode })
              : t('No currency set')}
          </p>
        </div>
      </div>

      <Well className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[11px] font-medium text-fg-muted">{t('Budget')}</span>
          <span className="text-sm font-medium tabular-nums text-fg-strong">
            {budgetAmount != null ? formatCurrency(Number(budgetAmount), currency) : '—'}
          </span>
        </div>

        {budgetReading.hasBudget ? (
          <>
            {/* Clamped bar, real numbers beneath it. */}
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
              <div
                className={`h-full transition-all duration-300 ease-slide ${
                  budgetReading.over ? 'bg-danger-500' : 'bg-champagne'
                }`}
                style={{ width: `${budgetReading.barPct}%` }}
              />
            </div>
            <div className="flex items-baseline justify-between gap-3 text-[11px]">
              <span className={budgetReading.over ? 'text-danger-600 dark:text-danger-400' : 'text-fg-muted'}>
                {t('{{pct}}% used', { pct: budgetReading.usedPct })}
              </span>
              <span
                className={`tabular-nums ${
                  budgetReading.remaining != null && budgetReading.remaining < 0
                    ? 'text-danger-600 dark:text-danger-400 font-medium'
                    : 'text-fg-body'
                }`}
              >
                {budgetReading.remaining != null
                  ? t('{{amount}} left', {
                    amount: formatCurrency(budgetReading.remaining, currency),
                  })
                  : ''}
              </span>
            </div>
          </>
        ) : economics == null && budgetAmount != null && Number(budgetAmount) > 0 ? (
          // The budget is known but usage isn't loaded yet - not "no budget".
          null
        ) : (
          <p className="text-[11px] text-fg-muted">
            {canManage
              ? t('No budget set. Add one to track how much has been used.')
              : t('No budget set for this matter.')}
          </p>
        )}
      </Well>

      {/* Only the terms this model actually has. */}
      {(requiresFixedFee(model) || requiresContingency(model)) && (
        <FieldGrid columns={2}>
          {requiresFixedFee(model) && (
            <Field
              label={t('Fixed fee amount')}
              value={matterData?.fixed_fee_amount != null
                ? formatCurrency(Number(matterData.fixed_fee_amount), currency)
                : undefined}
              emptyText="—"
            />
          )}
          {requiresContingency(model) && (
            <Field
              label={t('Contingency %')}
              value={matterData?.contingency_pct != null
                ? `${Number(matterData.contingency_pct) * 100}%`
                : undefined}
              emptyText="—"
            />
          )}
        </FieldGrid>
      )}
    </div>
  );
};

export default MatterPricingSummary;
